import { useEffect, useState } from "react";
import { api, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Pencil, TrendingUp, TrendingDown, Calculator, BarChart3, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const CATEGORIES = [
    { v: "one_off", label: "One-off / non-recurring" },
    { v: "owner_compensation", label: "Owner compensation" },
    { v: "personal_expense", label: "Personal expense" },
    { v: "accounting_normalisation", label: "Accounting normalisation" },
    { v: "synergy", label: "Synergy / cost-out" },
    { v: "other", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.v, c.label]));

export default function NormalisationTab({ deal, onRefresh }) {
    const [adjustments, setAdjustments] = useState([]);
    const [summary, setSummary] = useState({
        reported_ebitda: deal.reported_ebitda ?? null,
        addbacks_total: 0,
        addbacks_count: 0,
        normalised_ebitda: deal.normalised_ebitda ?? null,
    });
    const [loading, setLoading] = useState(true);
    const [reportedDraft, setReportedDraft] = useState(
        deal.reported_ebitda != null ? String(deal.reported_ebitda) : ""
    );
    const [savingReported, setSavingReported] = useState(false);
    const currency = deal.currency || "GBP";

    const refresh = async () => {
        const { data } = await api.get(`/ebitda/deal/${deal.id}`);
        setAdjustments(data.adjustments);
        setSummary(data.summary);
        setReportedDraft(data.summary.reported_ebitda != null ? String(data.summary.reported_ebitda) : "");
        setLoading(false);
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [deal.id]);

    const saveReported = async () => {
        setSavingReported(true);
        try {
            const value = reportedDraft.trim() === "" ? null : parseFloat(reportedDraft);
            const { data } = await api.patch(`/ebitda/deal/${deal.id}/reported`, { reported_ebitda: value });
            setSummary(data.summary);
            toast.success("Reported EBITDA saved");
            onRefresh?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setSavingReported(false);
        }
    };

    const onAdjustmentChange = (newSummary) => {
        setSummary(newSummary);
        onRefresh?.();
    };

    const reportedDisplay = summary.reported_ebitda != null
        ? fmtMoney(summary.reported_ebitda, currency) : "—";
    const normalisedDisplay = summary.normalised_ebitda != null
        ? fmtMoney(summary.normalised_ebitda, currency) : "—";
    const totalSign = summary.addbacks_total >= 0 ? "+" : "";

    return (
        <div className="space-y-4 max-w-5xl" data-testid="normalisation-tab">
            {/* Headline summary bar — Reported / Adjustments / Normalised */}
            <div className="border border-border rounded-md bg-white p-6">
                <div className="text-[10px] uppercase tracking-wider text-gold font-medium mb-3 flex items-center gap-1.5">
                    <Calculator className="w-3 h-3" /> EBITDA normalisation
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div data-testid="kpi-reported">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reported EBITDA</div>
                        <div className="text-2xl font-bold font-mono text-navy mt-1">{reportedDisplay}</div>
                        <div className="text-[10px] text-muted-foreground italic mt-0.5">From management accounts — never overwritten</div>
                    </div>
                    <div data-testid="kpi-addbacks" className="border-l border-border pl-4">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net adjustments ({summary.addbacks_count})</div>
                        <div className={`text-2xl font-bold font-mono mt-1 ${summary.addbacks_total >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {totalSign}{fmtMoney(summary.addbacks_total, currency)}
                        </div>
                        <div className="text-[10px] text-muted-foreground italic mt-0.5">Sum of all add-backs and deductions</div>
                    </div>
                    <div data-testid="kpi-normalised" className="border-l border-gold/40 pl-4">
                        <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">Normalised EBITDA</div>
                        <div className="text-2xl font-bold font-mono text-gold mt-1">{normalisedDisplay}</div>
                        <div className="text-[10px] text-muted-foreground italic mt-0.5">Used by the valuation tool (deal.ebitda)</div>
                    </div>
                </div>
                {summary.missing_baseline && (
                    <div
                        className="mt-4 flex items-start gap-2 px-3 py-2 border border-amber-200 bg-amber-50 rounded-md text-xs"
                        data-testid="normalisation-missing-baseline-banner"
                    >
                        <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                        <span className="text-amber-900">
                            {summary.addbacks_count} adjustment{summary.addbacks_count === 1 ? "" : "s"} recorded, but no Reported EBITDA
                            baseline is set yet — add-backs alone aren't a normalised EBITDA figure. Set the baseline below.
                        </span>
                    </div>
                )}
                {/* Inline edit for reported */}
                <div className="mt-5 pt-4 border-t border-border flex items-end gap-3">
                    <div className="space-y-1.5 flex-1 max-w-xs">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reported EBITDA ({currency})</Label>
                        <Input
                            type="number"
                            step="any"
                            className="font-mono"
                            value={reportedDraft}
                            onChange={(e) => setReportedDraft(e.target.value)}
                            placeholder="From management accounts"
                            data-testid="reported-ebitda-input"
                        />
                    </div>
                    <Button
                        onClick={saveReported}
                        disabled={savingReported}
                        className="bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                        data-testid="save-reported-ebitda-btn"
                    >
                        {savingReported ? "Saving…" : "Save reported"}
                    </Button>
                </div>
            </div>

            {/* Adjustments list */}
            <div className="border border-border rounded-md bg-white">
                <div className="px-6 py-4 flex items-center justify-between border-b border-border">
                    <div>
                        <h3 className="font-display font-bold tracking-tight">Add-back schedule</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Positive amounts add back to EBITDA. Negative amounts deduct (e.g. one-off gains).
                        </p>
                    </div>
                    <AdjustmentDialog
                        dealId={deal.id}
                        currency={currency}
                        onSaved={(s) => { refresh(); onAdjustmentChange(s); }}
                    />
                </div>
                {/* Waterfall bridge — only render when normalisation is engaged */}
                {summary.reported_ebitda != null && adjustments.length > 0 && (
                    <Waterfall
                        reported={summary.reported_ebitda}
                        adjustments={adjustments}
                        normalised={summary.normalised_ebitda}
                        currency={currency}
                    />
                )}
                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                ) : adjustments.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground" data-testid="adjustments-empty">
                        No add-backs yet. Use <span className="text-foreground font-medium">+ Adjustment</span> to record owner compensation, one-offs or accounting normalisations.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="adjustments-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-6 py-3">Label</th>
                                <th className="text-left px-6 py-3">Category</th>
                                <th className="text-right px-6 py-3">Amount</th>
                                <th className="text-left px-6 py-3">Notes</th>
                                <th className="px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence>
                                {adjustments.map((a) => (
                                    <motion.tr
                                        key={a.id}
                                        layout
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        className="border-b border-border/60 last:border-0 hover:bg-muted/30"
                                        data-testid={`adjustment-row-${a.id}`}
                                    >
                                        <td className="px-6 py-3">
                                            <div className="font-medium">{a.label}</div>
                                            {a.created_by_name && (
                                                <div className="text-[10px] text-muted-foreground font-mono">by {a.created_by_name}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3">
                                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono">
                                                {CAT_LABEL[a.category] || a.category}
                                            </Badge>
                                        </td>
                                        <td className={`px-6 py-3 text-right font-mono ${a.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                            <span className="inline-flex items-center gap-1">
                                                {a.amount >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                {a.amount >= 0 ? "+" : ""}{fmtMoney(a.amount, currency)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-xs text-muted-foreground max-w-md truncate">{a.notes || "—"}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <AdjustmentDialog
                                                dealId={deal.id}
                                                currency={currency}
                                                existing={a}
                                                onSaved={(s) => { refresh(); onAdjustmentChange(s); }}
                                            />
                                            <DeleteAdjustmentBtn
                                                adj={a}
                                                onDeleted={(s) => { refresh(); onAdjustmentChange(s); }}
                                            />
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function AdjustmentDialog({ dealId, currency, existing, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        label: existing?.label || "",
        amount: existing?.amount != null ? String(existing.amount) : "",
        category: existing?.category || "one_off",
        notes: existing?.notes || "",
    });
    const [loading, setLoading] = useState(false);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) return toast.error("Label required");
        if (form.amount === "" || isNaN(parseFloat(form.amount))) return toast.error("Amount required");
        setLoading(true);
        try {
            const payload = {
                label: form.label.trim(),
                amount: parseFloat(form.amount),
                category: form.category,
                notes: form.notes.trim() || null,
            };
            let resp;
            if (existing) {
                resp = await api.patch(`/ebitda/${existing.id}`, payload);
            } else {
                resp = await api.post(`/ebitda/deal/${dealId}`, payload);
            }
            toast.success(existing ? "Adjustment updated" : "Adjustment added");
            setOpen(false);
            onSaved?.(resp.data.summary);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {existing ? (
                    <Button size="icon" variant="ghost" data-testid={`edit-adjustment-${existing.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                ) : (
                    <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white gap-1.5" data-testid="new-adjustment-btn">
                        <Plus className="w-4 h-4" /> Adjustment
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent data-testid="adjustment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{existing ? "Edit adjustment" : "Add EBITDA adjustment"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Label *</Label>
                        <Input
                            required
                            value={form.label}
                            onChange={setF("label")}
                            placeholder="e.g. Owner's excess salary"
                            data-testid="adjustment-label-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                Amount (positive = add-back, negative = deduction) *
                            </Label>
                            <Input
                                required
                                type="number"
                                step="any"
                                className="font-mono"
                                value={form.amount}
                                onChange={setF("amount")}
                                placeholder={`e.g. 50000 or -25000 (${currency})`}
                                data-testid="adjustment-amount-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                            <Select value={form.category} onValueChange={setF("category")}>
                                <SelectTrigger data-testid="adjustment-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                        <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={setF("notes")}
                            placeholder="Rationale, supporting evidence, accountant sign-off…"
                            data-testid="adjustment-notes-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                            data-testid="adjustment-submit-btn"
                        >
                            {loading ? "Saving…" : existing ? "Save changes" : "Add adjustment"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteAdjustmentBtn({ adj, onDeleted }) {
    const [loading, setLoading] = useState(false);
    const remove = async () => {
        if (!confirm(`Delete "${adj.label}"? This will recalculate normalised EBITDA.`)) return;
        setLoading(true);
        try {
            const { data } = await api.delete(`/ebitda/${adj.id}`);
            toast.success("Adjustment removed");
            onDeleted?.(data.summary);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <Button size="icon" variant="ghost" onClick={remove} disabled={loading} data-testid={`delete-adjustment-${adj.id}`}>
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
    );
}


function Waterfall({ reported, adjustments, normalised, currency }) {
    // Build cumulative bridge: Reported → +/− each adjustment → Normalised
    const sortedAdj = [...adjustments].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    let running = reported;
    const steps = [{ label: "Reported", value: reported, kind: "anchor", running }];
    for (const a of sortedAdj) {
        running += a.amount;
        steps.push({ label: a.label, value: a.amount, kind: a.amount >= 0 ? "pos" : "neg", running });
    }
    steps.push({ label: "Normalised", value: normalised, kind: "anchor", running: normalised });

    const allValues = steps.map((s) => Math.max(s.running, 0));
    const ymax = Math.max(...allValues, 1);

    return (
        <div className="border-b border-border bg-muted/20" data-testid="ebitda-waterfall">
            <div className="px-6 pt-4 pb-2 flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5 text-gold" />
                <h4 className="text-xs uppercase tracking-wider text-gold font-semibold">EBITDA bridge</h4>
                <span className="text-[10px] text-muted-foreground italic">Reported → adjustments → normalised</span>
            </div>
            <div className="px-6 pb-5">
                <div className="flex items-end gap-2 h-40">
                    {steps.map((s, i) => {
                        const heightPct = Math.max(2, (s.running / ymax) * 100);
                        // For non-anchor steps, the visible "delta" segment height
                        const prevRunning = i > 0 ? steps[i - 1].running : 0;
                        const deltaTop = (Math.max(s.running, prevRunning) / ymax) * 100;
                        const deltaBot = (Math.min(s.running, prevRunning) / ymax) * 100;
                        const deltaHeight = Math.max(2, deltaTop - deltaBot);
                        const isAnchor = s.kind === "anchor";
                        const fill = isAnchor ? "bg-navy" : s.kind === "pos" ? "bg-emerald-500" : "bg-red-500";
                        return (
                            <div key={i} className="flex-1 flex flex-col items-center min-w-0" data-testid={`waterfall-step-${i}`}>
                                <div className="flex-1 w-full flex items-end relative">
                                    {isAnchor ? (
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: `${heightPct}%` }}
                                            transition={{ duration: 0.4, delay: i * 0.08 }}
                                            className={`w-full ${fill} rounded-t-sm`}
                                            title={`${s.label}: ${currency} ${s.value.toLocaleString()}`}
                                        />
                                    ) : (
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{
                                                height: `${deltaHeight}%`,
                                                marginBottom: `${deltaBot}%`,
                                            }}
                                            transition={{ duration: 0.4, delay: i * 0.08 }}
                                            className={`w-full ${fill} rounded-sm opacity-90`}
                                            title={`${s.label}: ${s.value >= 0 ? "+" : ""}${currency} ${s.value.toLocaleString()}`}
                                        />
                                    )}
                                </div>
                                <div className="mt-1 text-[9px] uppercase tracking-wider text-muted-foreground truncate w-full text-center font-mono">
                                    {s.label.length > 14 ? s.label.slice(0, 12) + "…" : s.label}
                                </div>
                                <div className={`text-[10px] font-mono font-medium truncate w-full text-center ${isAnchor ? "text-navy" : s.kind === "pos" ? "text-emerald-700" : "text-red-700"}`}>
                                    {isAnchor
                                        ? fmtMoney(s.value, currency)
                                        : `${s.value >= 0 ? "+" : ""}${fmtMoney(s.value, currency)}`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
